import { useSearchParams } from 'react-router';
import { PERIOD_PARAMS } from '@/shared/lib';
import { Button } from './Button';

/** A key field select of the bar: its URL parameter, label, "Any …" text and choices. */
export interface ViewFilterKey {
  param: string;
  label: string;
  /** Text of the empty choice, e.g. "Any license plan". */
  any: string;
  options: readonly { value: string; label: string }[];
}

interface ViewFilterBarProps {
  /**
   * Label of the date field that Year, Quarter and Month narrow ("Started"); without it the bar
   * has no period selects.
   */
  dateLabel?: string;
  keys: readonly ViewFilterKey[];
}

const FIRST_YEAR = 2014;
const QUARTERS = [1, 2, 3, 4];
const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const selectClass = (value: string) =>
  value ? 'select select--compact is-active' : 'select select--compact';

/**
 * The filter bar of a Data views page: Year, Quarter and Month of a date field, then key fields.
 * The choices live in the page URL; Quarter and Month need a year, and Month offers the months of
 * the chosen quarter.
 */
export function ViewFilterBar({ dateLabel, keys }: ViewFilterBarProps) {
  const [params, setParams] = useSearchParams();
  const year = params.get('year') ?? '';
  const quarter = params.get('quarter') ?? '';
  const month = params.get('month') ?? '';
  const all = [...(dateLabel ? PERIOD_PARAMS : []), ...keys.map((k) => k.param)];
  const active = all.some((k) => params.get(k));
  const years = Array.from(
    { length: new Date().getFullYear() - FIRST_YEAR + 1 },
    (_, i) => new Date().getFullYear() - i,
  );
  const months = MONTHS.map((name, i) => ({ name, n: i + 1 })).filter(
    (m) => !quarter || Math.ceil(m.n / 3) === Number(quarter),
  );

  const set = (changes: Record<string, string>) => {
    setParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        for (const [k, v] of Object.entries(changes)) {
          if (v) next.set(k, v);
          else next.delete(k);
        }
        return next;
      },
      { replace: true },
    );
  };

  return (
    <div className="toolbar" role="group" aria-label="View filters">
      {dateLabel && (
        <>
          <label className="visually-hidden" htmlFor="dv-year">
            Year
          </label>
          <select
            className={selectClass(year)}
            id="dv-year"
            value={year}
            // Without a year, quarter and month have no period to narrow.
            onChange={(e) => {
              set(e.target.value ? { year: e.target.value } : { year: '', quarter: '', month: '' });
            }}
          >
            <option value="">{dateLabel} · any year</option>
            {years.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
          <label className="visually-hidden" htmlFor="dv-quarter">
            Quarter
          </label>
          <select
            className={selectClass(quarter)}
            id="dv-quarter"
            value={quarter}
            disabled={!year}
            // A month of another quarter no longer fits.
            onChange={(e) => {
              const q = e.target.value;
              const keep = month && q && Math.ceil(Number(month) / 3) === Number(q);
              set({ quarter: q, month: keep ? month : '' });
            }}
          >
            <option value="">Any quarter</option>
            {QUARTERS.map((q) => (
              <option key={q} value={q}>
                Q{q}
              </option>
            ))}
          </select>
          <label className="visually-hidden" htmlFor="dv-month">
            Month
          </label>
          <select
            className={selectClass(month)}
            id="dv-month"
            value={month}
            disabled={!year}
            onChange={(e) => {
              set({ month: e.target.value });
            }}
          >
            <option value="">Any month</option>
            {months.map((m) => (
              <option key={m.n} value={m.n}>
                {m.name}
              </option>
            ))}
          </select>
        </>
      )}
      {keys.map((k) => {
        const value = params.get(k.param) ?? '';
        const id = `dv-${k.param}`;
        // A value from the link that is not (yet) among the choices stays selectable.
        const options =
          value && !k.options.some((o) => o.value === value)
            ? [...k.options, { value, label: value }]
            : k.options;
        return (
          <span key={k.param}>
            <label className="visually-hidden" htmlFor={id}>
              {k.label}
            </label>
            <select
              className={selectClass(value)}
              id={id}
              value={value}
              onChange={(e) => {
                set({ [k.param]: e.target.value });
              }}
            >
              <option value="">{k.any}</option>
              {options.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </span>
        );
      })}
      {active && (
        <Button
          variant="link"
          size="small"
          onClick={() => {
            set(Object.fromEntries(all.map((k) => [k, ''])));
          }}
        >
          Clear filters
        </Button>
      )}
    </div>
  );
}
